import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Tabs,
  Tab,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import { Star as StarIcon } from "@mui/icons-material";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import PersonalForm from "./PersonalForm";
import FinanceForm from "./FinanceForm";
import OperationsForm from "./OperationsForm";
import OrganisationForm from "./OrganisationForm";
import { useDispatch, useSelector } from "react-redux";
import Decrypt from "../../../shared/utils/Decrypt";
import { updateVendorProfileData } from "../../../redux/slices/vendorProfileSlice";
import useFetchCities from "../registerFormsFolder/useFetchCities";
import { statecityData } from "../../../shared/enums";
import { verifyGST } from "../../../shared/utils/gstInfoApi";
import axios from "axios";
import { Snackbar, Alert, CircularProgress } from "@mui/material";
import capitalizeWords from "../../../helperFunctions/capitalizedWords";

const VendorProfile = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { vendorProfileDetails } = useSelector((state) => state.vendorProfile);
  const [activeTab, setActiveTab] = useState(0); // Index of "Finance"
  const tabs = ["Personal", "Organisation", "Operations", "Finance"];
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const { vendorId } = useSelector((state) => state.vendorAuth);
  const [errors, setErrors] = useState({});
  const [isGstVerified, setIsGstVerified] = useState(true);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [hasUserEditedGSTIN, setHasUserEditedGSTIN] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  //api and fetch details from GSTIN API
  const gstin = formData?.GSTIN;

  // call everytime when the gst length is 15
  useEffect(() => {
    if (gstin && gstin.length === 15 && hasUserEditedGSTIN) {
      fetchDetailsThroughGSTIN(gstin.toUpperCase());
    }
  }, [gstin]);
  const cityApiKey = process.env.REACT_APP_FETCH_STATE_CITY_API_KEY;
  const cityArray = useFetchCities(formData?.state, statecityData, cityApiKey);

  // GST APi
  const fetchDetailsThroughGSTIN = async (gstNumber) => {
    setLoading(true);

    const responseData = await verifyGST(gstNumber);
    setLoading(false);
    console.log(responseData);

    if (responseData.error) {
      setErrors((prev) => ({
        ...prev,
        GSTIN: "Enter a valid 15-digit GSTIN",
      }));
      setIsGstVerified(false);
      return;
    }

    setIsGstVerified(true);

    const taxpayerInfo = responseData?.taxpayerInfo?.pradr?.addr || {};

    // Build address string
    const addressParts = [
      taxpayerInfo.flno,
      taxpayerInfo.bno,
      taxpayerInfo.st,
      taxpayerInfo.landMark,
      taxpayerInfo.locality,
      taxpayerInfo.loc,
    ].filter(Boolean);

    // Update formData locally
    setFormData((prev) => ({
      ...prev,
      GSTIN: gstNumber,
      state: taxpayerInfo.stcd || "",
      pinCode: taxpayerInfo.pncd || "",
      vendorBusinessName:
        capitalizeWords(responseData?.taxpayerInfo?.tradeNam) || "",
      address: capitalizeWords(addressParts.join(", ")),
      city: taxpayerInfo.dst || "",
    }));
  };

  useEffect(() => {
    if (vendorProfileDetails && Object.keys(vendorProfileDetails).length > 0) {
      const decrypted = {
        ...vendorProfileDetails,
        ownerName: Decrypt(vendorProfileDetails.ownerName || ""),
        ownerPrimaryContactNo: Decrypt(
          vendorProfileDetails.ownerPrimaryContactNo || "",
        ),
        GSTIN: Decrypt(vendorProfileDetails.GSTIN || ""),
      };
      setFormData(decrypted);
      setOriginalData(decrypted);
    }
  }, [vendorProfileDetails]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validations...
    if (name === "ownerName") {
      if (value.startsWith(" ") || !/^[a-zA-Z\s]*$/.test(value)) return;
    }

    if (
      (name === "ownerPrimaryContactNo" ||
        name === "ownerSecondaryContactNo") &&
      (!/^\d*$/.test(value) || value.length > 10)
    ) {
      return;
    }

    if (name === "aadhaarCardNumber") {
      const isValid = /^\d*$/.test(value);
      if (!isValid || value.length > 12) {
        return;
      }
    }

    if (name === "GSTIN") {
      if (value.length > 15) return;
      if (value.startsWith(" ")) return;
      setHasUserEditedGSTIN(true);

      const upperGSTIN = value ? value.toUpperCase() : "";

      setFormData((prev) => ({
        ...prev,
        [name]: upperGSTIN,
      }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      setHasChanges(
        isFormChanged({ ...formData, [name]: upperGSTIN }, originalData),
      );
      return;
    }

    if (name === "yearsInBusiness") {
      if (!/^\d*$/.test(value)) return; // Block non-integers
    }

    if (name === "pinCode") {
      // Allow only digits, max length 6
      if (!/^\d{0,6}$/.test(value)) return;
    }

    if (name === "bankAccountHolderName") {
      // If starts with a space or contains any digit, reject input
      if (/^\s/.test(value) || /\d/.test(value)) return;
    }

    if (name === "bankAccountNumber") {
      if (!/^\d*$/.test(value)) return;
    }

    if (name === "bankName") {
      if (/^\s/.test(value) || /\d/.test(value)) return;
    }

    if (name === "bankIFSCCode") {
      if (value.length > 11) return;
    }
    if (name === "PANNumber") {
      if (value.length > 10) return;
    }

    const updated = {
      ...formData,
      [name]: value,
    };

    setFormData(updated);
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setHasChanges(isFormChanged(updated, originalData));
  };

  const validate = () => {
    const newErrors = {};

    // validation for ownername
    if (!formData?.ownerName || formData.ownerName.trim() === "") {
      newErrors.ownerName = "Full Name is required";
    }

    //validation of email address
    if (!(formData?.emailAddress || "").trim()) {
      newErrors.emailAddress = "Email is required";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((formData.emailAddress || "").trim())
    ) {
      newErrors.emailAddress = "Please enter a valid email address";
    }

    // validation for primary contact number
    if (!formData?.ownerPrimaryContactNo.trim())
      newErrors.ownerPrimaryContactNo = "Primary Contact No is required";

    //validation for owner primary contact number should be 10 digit
    if (formData.ownerPrimaryContactNo.length !== 10) {
      newErrors.ownerPrimaryContactNo =
        "Required 10-digit primary contact number.";
    }

    //validation for the secondary number
    if (
      formData?.ownerSecondaryContactNo &&
      formData.ownerSecondaryContactNo.trim() !== ""
    ) {
      if (!/^\d{10}$/.test(formData.ownerSecondaryContactNo)) {
        newErrors.ownerSecondaryContactNo =
          "Secondary Contact No must be exactly 10 digits";
      }
    }

    //validation for GSTIN
    if (
      !formData?.GSTIN ||
      formData.GSTIN.trim().length !== 15 ||
      !isGstVerified
    ) {
      newErrors.GSTIN = "GSTIN must be exactly 15 characters";
    }

    //validation for vendor business name
    if (!formData?.vendorBusinessName) {
      newErrors.vendorBusinessName = "Organization name is required.";
    }

    //validation for address
    if (!formData?.address) {
      newErrors.address = "Organization address is required.";
    }

    // validation for pincode
    if (!formData?.pinCode || formData.pinCode.toString().length !== 6) {
      newErrors.pinCode = "Organization 6-digit pincode is required.";
    }

    if (!formData?.yearsInBusiness) {
      newErrors.yearsInBusiness = "Organization business years is required.";
    }

    if (!formData?.avgTurnoverLast3Years) {
      newErrors.avgTurnoverLast3Years = "Average turnover is required.";
    }

    if (!formData?.registeredInESIPF) {
      newErrors.registeredInESIPF = "ESIC/PF is required.";
    }

    if (!formData?.productionArea) {
      newErrors.productionArea = "Production area is required.";
    }

    if (!formData?.numberOfEmployees) {
      newErrors.numberOfEmployees = "Number of employees is required.";
    }

    if (!formData?.numberOfSkilledManpower) {
      newErrors.numberOfSkilledManpower =
        "Number of skilled employees is required.";
    }

    if (!formData?.numberOfUnskilledManpower) {
      newErrors.numberOfUnskilledManpower =
        "Number of unskilled employees is required.";
    }

    if (formData?.scalingFactors.length === 0) {
      newErrors.scalingFactors = "Scaling factor is required.";
    }

    if (formData?.bankIFSCCode && formData?.bankIFSCCode.length < 11) {
      newErrors.bankIFSCCode = "Bank 11-digit IFSC code is required.";
    }

    if (formData?.PANNumber && formData?.PANNumber.length < 10) {
      newErrors.PANNumber = "10-digit Pan code is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormChanged = (newData, oldData) => {
    for (const key in newData) {
      if ((newData[key] || "") !== (oldData[key] || "")) {
        return true;
      }
    }
    return false;
  };

  const handleSave = () => {
    console.log("error", errors);

    if (!validate()) {
      setSnackbar({
        open: true,
        message: "Please fill all the required fields.",
        severity: "error",
      });
      return;
    }
    dispatch(updateVendorProfileData({ vendorId, formData }));
    setOriginalData(formData); // update snapshot
    setHasChanges(false);
  };

  //function for the Image upload
  const handleFileUpload = async (e, fieldName) => {
    const imageFile = e.target.files[0];
    if (!imageFile) return;

    const allowed = ["png", "jpg", "jpeg"];
    const ext = imageFile.name.split(".").pop().toLowerCase();
    if (!allowed.includes(ext)) {
      setSnackbar({
        open: true,
        message: "Only JPG / JPEG / PNG allowed.",
        severity: "error",
      });
      return;
    }
    if (imageFile.size > 2 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: "Image must be ≤ 2 MB",
        severity: "error",
      });
      return;
    }

    const fd = new FormData();
    fd.append("files", imageFile);

    try {
      setPhotoUploading(true);
      const token = localStorage.getItem("vendorToken");
      const res = await axios.post(`${process.env.REACT_APP_URL}/upload`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res?.data?.success) {
        const url = res.data.data[0];

        setFormData((prev) => ({ ...prev, [fieldName]: url }));
        await dispatch(
          updateVendorProfileData({
            vendorId,
            formData: { ...formData, [fieldName]: url },
          }),
        );
        setSnackbar({
          open: true,
          message: "Image uploaded successfully.",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Upload failed. Try again.",
          severity: "error",
        });
      }
      setPhotoUploading(false);
    } catch (err) {
      console.error(err);
      setPhotoUploading(false);
      setSnackbar({
        open: true,
        message: "Error uploading image.",
        severity: "error",
      });
    } finally {
      setPhotoUploading(false);
    }
  };

  const renderActiveForm = () => {
    switch (activeTab) {
      case 0:
        return (
          <PersonalForm
            handleSave={handleSave}
            handleChange={handleChange}
            formData={formData}
            errors={errors}
            hasChanges={hasChanges}
          />
        );
      case 1:
        return (
          <OrganisationForm
            handleSave={handleSave}
            handleChange={handleChange}
            formData={formData}
            errors={errors}
            hasChanges={hasChanges}
            cityArray={cityArray}
            loading={loading}
            isGstVerified={isGstVerified}
          />
        );
      case 2:
        return (
          <OperationsForm
            handleSave={handleSave}
            handleChange={handleChange}
            formData={formData}
            errors={errors}
            hasChanges={hasChanges}
          />
        );
      case 3:
        return (
          <FinanceForm
            handleSave={handleSave}
            handleChange={handleChange}
            formData={formData}
            errors={errors}
            hasChanges={hasChanges}
            handleFileUpload={handleFileUpload}
            setFormData={setFormData}
          />
        );
      default:
        return null;
    }
  };

  const getInitials = (name) => {
    if (!name) return "NA";
    const parts = name.trim().split(" ").filter(Boolean);
    const initials = parts
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
    return initials || "NA";
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        minHeight: "87vh",
        backgroundColor: "#F4F4F7",
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{ width: { xs: "100%", sm: "100%", md: "100%", lg: 320 }, p: 2 }}
      >
        <Card variant="elevation" sx={{ borderRadius: 5 }}>
          <CardContent sx={{ textAlign: "center" }}>
            {/* Avatar with tick */}
            <Box sx={{ position: "relative", display: "inline-block", mb: 2 }}>
              {/* Avatar Container */}
              <Box sx={{ position: "relative", width: 80, height: 80 }}>
                <Avatar
                  src={
                    formData?.profilePhoto
                      ? formData.profilePhoto
                      : "/placeholder.svg?height=80&width=80"
                  }
                  sx={{ width: 80, height: 80, mx: "auto" }}
                >
                  {!vendorProfileDetails?.profilePhoto &&
                    getInitials(Decrypt(vendorProfileDetails?.ownerName))}
                </Avatar>

                {/* Circular loader in center of Avatar */}
                {photoUploading && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                )}
              </Box>

              {/* Hidden file input */}
              <input
                type="file"
                accept="image/*"
                id="upload-profile-photo"
                style={{ display: "none" }}
                onChange={(e) => handleFileUpload(e, "profilePhoto")}
              />

              {/* Edit Icon */}
              <label htmlFor="upload-profile-photo">
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    bgcolor: "primary.main",
                    color: "white",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <DriveFileRenameOutlineIcon sx={{ fontSize: 14 }} />
                </Box>
              </label>
            </Box>

            {/* Info */}
            <Tooltip title={originalData?.ownerName || ""} placement="top">
              <Typography variant="h6" noWrap>
                {originalData?.ownerName
                  ? originalData.ownerName.length > 10
                    ? `${originalData.ownerName.slice(0, 10)}...`
                    : originalData.ownerName
                  : ""}
              </Typography>
            </Tooltip>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {originalData?.ownerPrimaryContactNo}
            </Typography>

            {/* Rating */}
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mb={2}
            >
              <StarIcon sx={{ fontSize: 16, color: "#facc15", mr: 0.5 }} />
              <Typography variant="body2">
                {originalData?.reliability || "0"}
              </Typography>
            </Box>

            {/* Details */}
            <Box
              textAlign="center"
              sx={{
                mt: 2,
                backgroundColor: "#f9fafb",
                borderRadius: 5,
                padding: 1,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {`#${vendorProfileDetails?._id?.slice(-6)}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Vendor Code
              </Typography>

              <Typography variant="body2" fontWeight={600} mt={2}>
                {vendorProfileDetails?.createdAt
                  ? new Date(vendorProfileDetails.createdAt)
                      .toISOString()
                      .split("T")[0]
                  : "N/A"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Joining Date
              </Typography>

              <Typography variant="body2" fontWeight={600} mt={2}>
                {originalData?.emailAddress}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Email
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Main Content */}
      <Box variant="elevation" flex={1} p={2}>
        <Box sx={{ borderRadius: 5, backgroundColor: "white", boxShadow: 1 }}>
          <Box
            sx={{
              backgroundColor: "#F8FAFC",
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
            }}
          >
            <Box px={2} pt={2}>
              <Typography
                variant="h4"
                sx={{ font: 800, fontSize: "30px", fontWeight: "bold" }}
              >
                Profile
              </Typography>
            </Box>

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              sx={{ mb: 1 }}
            >
              {tabs.map((tab, index) => (
                <Tab label={tab} key={tab} sx={{ fontWeight: "semiBold" }} />
              ))}
            </Tabs>
          </Box>

          {/* Finance Section */}
          <Box mt={2}>{renderActiveForm()}</Box>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            backgroundColor:
              snackbar.severity === "success"
                ? "var(--third-color)"
                : "var(--heading-color)",
            color: "var(--secondary-color)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorProfile;
